import { LazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import { useImageLoader } from './hooks';
import './LazyLoadWrapper.scss';

export interface LazyLoadWrapperProps extends LazyLoadImageProps {
  /** the alt text for the image */
  alt?: string;
  /** optional class name for the image */
  className?: string;
  /** fallback element to render if image fails to load */
  fallback?: React.ReactNode;
  /** whether to lazy load the image */
  lazy?: boolean;
  /**
   * props for react-lazy-load-image-component
   * https://github.com/Aljullu/react-lazy-load-image-component#props
   */
  lazyProps?: LazyLoadImageProps;
  /** the image source */
  src?: string;
  /** callback fired when image loading state changes */
  onImageStateChange?: (hasError: boolean) => void;
}

/**
 * LazyLoadWrapper - component that loads lazy images or a fallback element
 * @param alt - the alt text for the image
 * @param className - optional class name for the image
 * @param fallback - fallback element to render if image fails to load
 * @param lazy - whether to lazy load the image
 * @param lazyProps - props for react-lazy-load-image-component
 * @param src - the image source
 * @param onImageStateChange - callback fired when image loading state changes
 */
export const LazyLoadWrapper: React.FC<LazyLoadWrapperProps> = ({
  alt,
  className,
  fallback,
  lazy,
  lazyProps,
  src,
  onImageStateChange,
  ...rest
}) => {
  const { hasImageError, handleImageError } = useImageLoader();
  const hasImage = !hasImageError && src;

  const handleError = () => {
    handleImageError();
    if (onImageStateChange) onImageStateChange(true);
  };

  const imgProps = {
    alt,
    src,
    onError: handleError,
    className,
  };

  if (!hasImage) return <>{fallback}</>;

  return lazy ? (
    <LazyLoadImage effect="opacity" {...imgProps} {...rest} {...lazyProps} />
  ) : (
    <img {...imgProps} {...rest} />
  );
};
